const sharedFilterParams = new Set([
  'filter.v.availability', 'filter.v.price.gte', 'filter.v.price.lte', 'filter.p.vendor'
]);
const priceParamNames = ['filter.v.price.gte', 'filter.v.price.lte'];

export function parseShopAllPrice(raw) {
  const text = raw == null ? '' : String(raw).trim();
  if (text === '') return { kind: 'empty' };
  const value = Number(text);
  return /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text) && Number.isFinite(value) &&
    Number.isSafeInteger(Math.trunc(value))
    ? { kind: 'valid', value, text } : { kind: 'invalid' };
}

export function normaliseShopAllPriceParameters(parameters) {
  const params = new URLSearchParams(parameters);
  priceParamNames.forEach(key => {
    const values = params.getAll(key).map(parseShopAllPrice);
    if (!values.length) return;
    if (values.some(value => value.kind !== 'valid') ||
        values.some(value => value.value !== values[0].value)) params.delete(key);
    else params.set(key, values[0].text);
  });
  if (params.has(priceParamNames[0]) && params.has(priceParamNames[1]) &&
      Number(params.get(priceParamNames[0])) > Number(params.get(priceParamNames[1]))) {
    priceParamNames.forEach(key => params.delete(key));
  }
  return params;
}

export function readShopAllPriceKeys(currentUrl) {
  const params = normaliseShopAllPriceParameters(new URL(currentUrl).searchParams);
  // A direct URL is explicit context, including bounds equal to this category's
  // defaults. Do not guess whether an existing bookmarked budget was accidental.
  return new Set(priceParamNames.filter(key => params.has(key) && params.get(key).trim() !== ''));
}

export function readEditedShopAllPriceKeys(minimum, maximum, rangeMaximum) {
  const keys = new Set();
  const low = parseShopAllPrice(minimum), high = parseShopAllPrice(maximum);
  if (low.kind === 'valid' && low.value !== 0) keys.add(priceParamNames[0]);
  if (high.kind === 'valid' && high.value !== Number(rangeMaximum)) keys.add(priceParamNames[1]);
  return keys;
}

export function applyShopAllPriceKeys(parameters, keys) {
  const next = new URLSearchParams(parameters);
  priceParamNames.forEach(key => { if (!keys.has(key)) next.delete(key); });
  return normaliseShopAllPriceParameters(next);
}

export function readShopAllPath(currentUrl) {
  const current = new URL(currentUrl);
  const match = current.pathname.match(/^(.*\/collections\/all-products)(?:\/([^/]+))?\/?$/);
  if (!match) return null;
  return {
    cataloguePath: match[1],
    tags: match[2] ? decodeURIComponent(match[2]).split('+') : []
  };
}

export function buildShopAllResetUrl(currentUrl) {
  const next = new URL(currentUrl);
  if (!readShopAllPath(next.href)) throw new Error('Reset must stay within Shop All.');
  [...new Set(next.searchParams.keys())].forEach(key => {
    if (key.startsWith('filter.') ||
        ['page', 'sort_by', 'browse', '_t', 'section_id', 'sections'].includes(key)) {
      next.searchParams.delete(key);
    }
  });
  next.hash = '';
  return next.href;
}

export function buildShopAllUrl(destination, currentUrl, sortOverride = null) {
  const current = new URL(currentUrl);
  current.search = normaliseShopAllPriceParameters(current.searchParams).toString();
  const next = new URL(destination, current);
  const catalogueIndex = current.pathname.indexOf('/collections/all-products');
  const cataloguePath = current.pathname.slice(0, catalogueIndex) + '/collections/all-products';
  if (catalogueIndex < 0 || next.origin !== current.origin ||
      (next.pathname !== cataloguePath && !next.pathname.startsWith(cataloguePath + '/'))) {
    throw new Error('Shop All destination must stay within this catalogue.');
  }
  const leavingBestSellers = readShopAllPath(current.href)?.tags.length === 0 &&
    current.searchParams.get('sort_by') === 'best-selling' &&
    readShopAllPath(next.href)?.tags.length > 0;
  next.search = '';
  next.hash = '';
  current.searchParams.forEach((value, key) => {
    if (!['page', '_t', 'section_id', 'sections'].includes(key) &&
        !(leavingBestSellers && key === 'sort_by') &&
        (!key.startsWith('filter.') || sharedFilterParams.has(key))) {
      next.searchParams.append(key, value);
    }
  });
  if (sortOverride) next.searchParams.set('sort_by', sortOverride);
  return next.href;
}

export function readResultCount(grid) {
  const payload = grid.querySelector('[data-citadel-shop-all-count-text]');
  return payload ? payload.getAttribute('data-citadel-shop-all-count-text') : null;
}

export function buildShopAllPaginationUrl(destination, currentUrl) {
  const current = new URL(currentUrl);
  const next = new URL(destination, current);
  if (!/\/collections\/all-products(?:\/|$)/.test(current.pathname) ||
      next.origin !== current.origin || next.pathname !== current.pathname) return destination;
  const keys = new Set([...current.searchParams.keys()].filter(key => /^utm_[a-z0-9_]+$/i.test(key)));
  keys.forEach(key => {
    const values = current.searchParams.getAll(key).filter(Boolean);
    if (!values.length) return;
    next.searchParams.delete(key);
    values.forEach(value => next.searchParams.append(key, value));
  });
  return next.href;
}

let installation = null;

function installShopAll() {
  const navigation = document.querySelector('[data-citadel-shop-all]');
  const grid = document.querySelector('#collection_root [data-body-grid]');
  const count = document.querySelector('[data-citadel-shop-all-count]');
  if (!navigation || !grid || !count) {
    if (installation) installation.dispose();
    installation = null;
    return;
  }
  if (installation && installation.navigation === navigation &&
      installation.grid === grid && installation.count === count) { installation.rebind(); return; }
  if (installation) installation.dispose();

  const initialPath = readShopAllPath(window.location.href);
  let priceKeys = readShopAllPriceKeys(window.location.href);
  let dragPriceValues = null;
  let removingPrice = false;
  let nativeGetAjaxUrl = null;
  let priceForm = null;
  let priceSeeded = false;
  const priceBindings = new Map();
  const nativeWindowBindings = new Map();
  const readPriceInputs = () => {
    const minimum = navigation.filter_form?.querySelector('[name="filter.v.price.gte"]');
    const maximum = navigation.filter_form?.querySelector('[name="filter.v.price.lte"]');
    return minimum && maximum ? { minimum, maximum } : null;
  };
  const updateEditedPrice = () => {
    const inputs = readPriceInputs();
    if (inputs) [inputs.minimum, inputs.maximum].forEach(input => commitMonetaryInput(input));
  };
  const refreshPrice = () => {
    if (!navigation.active_price_tag) return;
    updateEditedPrice();
    const inputs = readPriceInputs();
    const low = inputs && parseShopAllPrice(inputs.minimum.value);
    const high = inputs && parseShopAllPrice(inputs.maximum.value);
    const active = !removingPrice && (priceKeys.has(priceParamNames[1]) ||
      (priceKeys.has(priceParamNames[0]) && low?.kind === 'valid' && low.value !== 0));
    navigation.render_active_price = active;
    navigation.active_price_tag.style.display = active ? '' : 'none';
    if (active && inputs) {
      navigation.active_min.textContent = priceKeys.has(priceParamNames[0]) && low.kind === 'valid' ? `${low.value}` : '0';
      navigation.active_max.textContent = priceKeys.has(priceParamNames[1]) && high.kind === 'valid' ? `${high.value}` : '';
    }
    const binding = priceBindings.get(navigation.active_price_tag);
    if (binding?.separator) binding.separator.textContent = priceKeys.has(priceParamNames[1]) ? binding.separatorText : '+';
    navigation.toggleActiveTags();
  };
  const onPriceIntent = event => {
    if (removingPrice) return;
    if (event.type !== 'change' && event.target.closest('.price-range--button')) {
      const inputs = readPriceInputs();
      const range = inputs?.minimum.closest('price-range-element');
      if (range?.prices) {
        syncNativePrices(range);
        range.getPriceRangeGeometry();
        // Drag bounds may meet; visual hit areas must not impose a money gap.
        range.button_min_delta = 0;
        dragPriceValues = { values: [inputs.minimum.value, inputs.maximum.value], positions: { ...range.price_positions } };
      }
    }
  };
  const onPriceRender = () => {
    const inputs = readPriceInputs();
    if (!removingPrice && dragPriceValues && inputs) {
      const range = inputs.minimum.closest('price-range-element');
      [inputs.minimum, inputs.maximum].forEach((input, index) => {
        const key = index === 0 ? 'min' : 'max';
        if (range.price_positions[key] === dragPriceValues.positions[key]) input.value = dragPriceValues.values[index];
        else commitMonetaryInput(input);
      });
      syncNativePrices(range);
      range.button_min_delta = 32;
    }
    dragPriceValues = null;
    refreshPrice();
  };
  const getPriceAwareAjaxUrl = function (...args) {
    if (!readShopAllPath(window.location.href)) return nativeGetAjaxUrl.apply(this, args);
    // Native clear emits changes for both inputs. Let it reset its own slider
    // state, then request the final unpriced result once in onRemovePrice.
    if (removingPrice) return;
    updateEditedPrice();
    const parameters = applyShopAllPriceKeys(new URLSearchParams(new FormData(this.filter_form)), priceKeys);
    // Preserve the native history, fetching, cancellation and rendering path.
    this.ajax_url = `${this.url}${parameters.toString()}`;
    history.replaceState({}, '', this.ajax_url);
    this.getGridHtml();
    refreshPrice();
  };
  const onRemovePrice = event => {
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    event.preventDefault();
    const binding = priceBindings.get(event.currentTarget);
    removingPrice = true;
    dragPriceValues = null;
    priceKeys.clear();
    try {
      // Keep the original theme:priceRange:clear event and native slider reset.
      binding[event.type === 'click' ? 'nativeClick' : 'nativeKeydown'].call(event.currentTarget, event);
    } finally {
      removingPrice = false;
    }
    navigation.showLoadingView();
    navigation.getAjaxUrl();
  };
  const bindPriceHandling = () => {
    if (!navigation.filter_form || typeof navigation.getAjaxUrl !== 'function') return;
    ['theme:navigation:loadNewProducts', 'theme:priceRange:renderActive'].forEach(type => {
      const previous = nativeWindowBindings.get(type), current = window.namespaces?.[type];
      if (previous && previous !== current) window.removeEventListener(type, previous);
      if (current) nativeWindowBindings.set(type, current);
    });
    if (!nativeGetAjaxUrl) {
      nativeGetAjaxUrl = navigation.getAjaxUrl;
      navigation.getAjaxUrl = getPriceAwareAjaxUrl;
    }
    if (priceForm !== navigation.filter_form) {
      priceForm?.removeEventListener('change', onPriceIntent, true);
      priceForm?.removeEventListener('mousedown', onPriceIntent, true);
      priceForm?.removeEventListener('touchstart', onPriceIntent, true);
      priceForm = navigation.filter_form;
      priceSeeded = false;
      priceForm.addEventListener('mousedown', onPriceIntent, true);
      priceForm.addEventListener('touchstart', onPriceIntent, { capture: true, passive: true });
    }
    const control = navigation.active_price_tag;
    if (control) {
      const nativeClick = control.namespaces?.click;
      const nativeKeydown = control.namespaces?.keydown;
      if (nativeClick && nativeClick === nativeKeydown) {
        control.removeEventListener('click', nativeClick);
        control.removeEventListener('keydown', nativeKeydown);
        if (!priceBindings.has(control)) {
          control.addEventListener('click', onRemovePrice);
          control.addEventListener('keydown', onRemovePrice);
          const separator = navigation.active_min.nextSibling;
          priceBindings.set(control, { separator: separator?.nodeType === 3 ? separator : null, separatorText: separator?.textContent });
        }
        Object.assign(priceBindings.get(control), { nativeClick, nativeKeydown });
      }
    }
    bindNumericInputs();
    if (!priceSeeded && numericInputs.size) {
      const current = new URL(window.location.href);
      const params = normaliseShopAllPriceParameters(current.searchParams);
      priceKeys = new Set(priceParamNames.filter(key => params.has(key)));
      const inputs = readPriceInputs();
      [inputs.minimum, inputs.maximum].forEach((input, index) => {
        input.value = params.get(input.name) ?? (current.searchParams.has(input.name) ? '' : index === 0 ? '0' : inputs.maximum.getAttribute('max'));
        input.setCustomValidity(''); Object.assign(numericInputs.get(input), { marker: input.value, error: '' });
      });
      priceSeeded = true;
      syncNativePrices(inputs.minimum.closest('price-range-element'));
      if (params.toString() !== current.searchParams.toString()) {
        current.search = params.toString(); history.replaceState({}, '', current.href);
        navigation.showLoadingView(); navigation.getAjaxUrl();
      }
    }
    refreshPrice();
  };
  // Native connectedCallback reads max before calling priceInputListeners.
  // Keep that DOM contract; replace only this instance's monetary input path.
  const ranges = new Map();
  const numericInputs = new Map();
  const commitMonetaryInput = (input, showError = false) => {
    const binding = numericInputs.get(input);
    if (!binding || removingPrice || (binding.marker === input.value &&
        (!showError || !binding.error))) return false;
    const parsed = parseShopAllPrice(input.value);
    const inputs = readPriceInputs();
    const other = input === inputs.minimum ? inputs.maximum : inputs.minimum;
    const otherValue = priceKeys.has(other.name) ? parseShopAllPrice(other.value) : null;
    let message = '';
    if (parsed.kind === 'invalid') message = 'Enter a finite, non-negative price using digits and a decimal point. This bound is not applied.';
    else if (parsed.kind === 'valid' && otherValue?.kind === 'valid' &&
        (input === inputs.minimum ? parsed.value > otherValue.value : parsed.value < otherValue.value)) {
      message = 'Minimum price must not exceed maximum price. This bound is not applied.';
    }
    const previous = priceKeys.has(input.name);
    if (message || parsed.kind === 'empty' ||
        (input === inputs.minimum ? parsed.value === 0 : parsed.value === Number(inputs.maximum.getAttribute('max')))) priceKeys.delete(input.name);
    else priceKeys.add(input.name);
    const changed = binding.marker !== input.value || previous !== priceKeys.has(input.name) || binding.error !== message;
    binding.marker = input.value;
    binding.error = message;
    input.setCustomValidity(message);
    if (showError && message) input.reportValidity();
    return changed;
  };
  const syncNativePrices = range => {
    const [minimum, maximum] = range.price_inputs;
    const min = parseShopAllPrice(minimum.value), max = parseShopAllPrice(maximum.value);
    const low = priceKeys.has(minimum.name) && min.kind === 'valid' ? min.value : 0;
    const high = priceKeys.has(maximum.name) && max.kind === 'valid' ? max.value : range.prices.max;
    Object.assign(range.prices, {
      current_min: Math.max(0, Math.min(low, range.prices.max)),
      current_max: Math.max(0, Math.min(Math.max(low, high), range.prices.max))
    });
    range.prices.pending_min = range.prices.current_min;
    range.prices.pending_max = range.prices.current_max;
    if (Number.isFinite(range.pixels_per_price)) {
      range.getPriceRangePositions(); range.movePriceRangeButtons(); range.setPriceRangeFill();
    }
  };
  const onNumericChange = event => {
    const input = event.currentTarget;
    const range = input.closest('price-range-element');
    const changed = commitMonetaryInput(input, true);
    dragPriceValues = null;
    syncNativePrices(range);
    range.renderActivePrice();
    if (changed || removingPrice) window.trigger('theme:navigation:loadNewProducts');
  };
  const onNumericKeydown = event => { if (event.key === 'Enter') { event.preventDefault(); onNumericChange(event); } };
  const onNumericDraft = event => event.currentTarget.setCustomValidity('');
  const bindNumericRange = range => {
    let binding = ranges.get(range);
    if (!binding) {
      binding = { inputListeners: range.priceInputListeners,
        resizeObserver: range.priceRangeResizeObserver, clearListener: range.clearPriceListener,
        dragBegin: range.dragBegin, clear: window.namespaces?.['theme:priceRange:clear'], pointers: new Map() };
      ranges.set(range, binding);
      range.priceInputListeners = () => bindNumericRange(range);
      // The original observer already follows this same line through relocation.
      range.priceRangeResizeObserver = () => {};
      range.dragBegin = event => {
        const point = event.type === 'touchstart' ? event.targetTouches[0] : event;
        const x = point.pageX - (window.scrollX || 0);
        const buttons = [...range.price_range_buttons];
        const target = buttons.reduce((nearest, button) => {
          const a = nearest.getBoundingClientRect(), b = button.getBoundingClientRect();
          const ac = (a.left + a.right) / 2, bc = (b.left + b.right) / 2;
          return Math.abs(x - bc) < Math.abs(x - ac) || (ac === bc && x > bc) ? button : nearest;
        });
        binding.dragBegin.call(range, { type: event.type, target, pageX: event.pageX, targetTouches: event.targetTouches });
      };
      range.clearPriceListener = () => {
        if (binding.clear) window.removeEventListener('theme:priceRange:clear', binding.clear);
        binding.clearListener.call(range);
        binding.clear = window.namespaces?.['theme:priceRange:clear'];
      };
      range.clearPriceListener();
    }
    range.price_inputs.forEach(input => {
      const old = numericInputs.get(input);
      const change = old?.nativeChange || input.namespaces?.change;
      const keydown = old?.nativeKeydown || input.namespaces?.keydown;
      if (change) input.removeEventListener('change', change);
      if (keydown) input.removeEventListener('keydown', keydown);
    });
    binding.inputListeners.call(range);
    range.price_inputs.forEach(input => {
      let inputBinding = numericInputs.get(input);
      if (!inputBinding) {
        inputBinding = { type: input.getAttribute('type'), mode: input.getAttribute('inputmode'), marker: input.value, error: '' };
        numericInputs.set(input, inputBinding);
        input.setAttribute('type', 'text');
        input.setAttribute('inputmode', 'decimal');
        input.addEventListener('change', onNumericChange);
        input.addEventListener('keydown', onNumericKeydown);
        input.addEventListener('input', onNumericDraft);
      }
      inputBinding.nativeChange = input.namespaces.change;
      inputBinding.nativeKeydown = input.namespaces.keydown;
      input.removeEventListener('change', inputBinding.nativeChange);
      input.removeEventListener('keydown', inputBinding.nativeKeydown);
    });
    range.price_range_buttons.forEach(button => {
      const previous = binding.pointers.get(button);
      const current = button.namespaces?.mousedown;
      if (previous && previous !== current) {
        button.removeEventListener('mousedown', previous);
        button.removeEventListener('touchstart', previous);
      }
      binding.pointers.set(button, current);
    });
    syncNativePrices(range);
  };
  const bindNumericInputs = () => {
    const inputs = readPriceInputs();
    const range = inputs?.minimum.closest('price-range-element');
    if (range?.prices) bindNumericRange(range);
  };
  const disposeNumericInputs = () => {
    numericInputs.forEach((binding, input) => {
      input.removeEventListener('change', onNumericChange);
      input.removeEventListener('keydown', onNumericKeydown);
      input.removeEventListener('input', onNumericDraft);
      input.setCustomValidity('');
      if (binding.nativeChange) input.addEventListener('change', binding.nativeChange);
      if (binding.nativeKeydown) input.addEventListener('keydown', binding.nativeKeydown);
      if (binding.type === null) input.removeAttribute('type'); else input.setAttribute('type', binding.type);
      if (binding.mode === null) input.removeAttribute('inputmode'); else input.setAttribute('inputmode', binding.mode);
    });
    ranges.forEach((binding, range) => {
      range.priceInputListeners = binding.inputListeners;
      range.priceRangeResizeObserver = binding.resizeObserver;
      range.clearPriceListener = binding.clearListener;
      range.dragBegin = binding.dragBegin;
      if (binding.clear) window.removeEventListener('theme:priceRange:clear', binding.clear);
    });
  };

  const systemControls = [...navigation.querySelectorAll('[data-tab][data-citadel-shop-all-destination]')]
    .map(link => ({ link, path: readShopAllPath(new URL(link.getAttribute('data-citadel-shop-all-destination'), window.location.href).href) }))
    .filter(({ path }) => path && path.tags.length === 1)
    .map(({ link, path }) => ({ link, tag: path.tags[0] }));
  // Read handles already supplied by Liquid; do not derive them from display labels.
  const subcategoryControls = [...navigation.querySelectorAll('[data-subtab][data-citadel-shop-all-destination]')]
    .map(link => {
      const path = readShopAllPath(new URL(link.getAttribute('data-citadel-shop-all-destination'), window.location.href).href);
      const system = systemControls.find(({ tag }) => path?.tags.includes(tag));
      const tag = path?.tags.find(value => value !== system?.tag) ||
        (link.getAttribute('aria-current') === 'page' && initialPath?.tags.find(value => value !== system?.tag));
      return { link, system, tag };
    }).filter(({ system, tag }) => system && tag);

  const refreshLinks = () => {
    navigation.querySelectorAll('[data-citadel-shop-all-destination]').forEach(link => {
      link.href = buildShopAllUrl(link.getAttribute('data-citadel-shop-all-destination'), window.location.href,
        link.getAttribute('data-citadel-shop-all-sort'));
    });
  };

  const refreshPagination = () => {
    document.querySelectorAll('#collection_pagination a[href]').forEach(link => {
      link.href = buildShopAllPaginationUrl(link.getAttribute('href'), window.location.href);
    });
  };

  const preserveNativeContext = () => {
    // The request path already contains Shopify's actual handles and tag order.
    // Rebuilding it from an entity-encoded tag label can introduce '-amp-'.
    const base = new URL(window.location.pathname, window.location.href);
    const context = new URL(window.location.href).searchParams;
    const keys = [...new Set([...context.keys()])].filter(key =>
      key.startsWith('utm_') || ['gclid', 'dclid', 'fbclid', 'msclkid', 'ttclid', 'preview_theme_id'].includes(key)
    );
    keys.forEach(key => {
      base.searchParams.delete(key);
      context.getAll(key).forEach(value => base.searchParams.append(key, value));
    });
    const url = base.pathname + base.search + (base.search ? '&' : '?');
    navigation.setAttribute('data-url', url);
    if (typeof navigation.url === 'string') navigation.url = url;
  };

  const refreshCount = () => {
    const result = readResultCount(grid);
    count.setAttribute('aria-busy', result === null ? 'true' : 'false');
    if (count.textContent !== (result ?? '')) count.textContent = result ?? '';
  };

  const refreshSort = () => {
    const selected = document.querySelector('.filter--root input[name="sort_by"]:checked');
    const label = navigation.querySelector('[data-citadel-shop-all-sort-label]');
    if (label) {
      label.textContent = selected
        ? selected.getAttribute('data-label') || label.textContent
        : label.getAttribute('data-citadel-shop-all-sort-default-label') || label.textContent;
    }
  };

  const refreshViews = () => {
    const all = navigation.querySelector('[data-tab="all"]');
    const best = navigation.querySelector('[data-tab="best-selling"]');
    if (!all || !best) return;
    const current = new URL(window.location.href);
    const atRoot = current.pathname === new URL(all.getAttribute('data-citadel-shop-all-destination'), current).pathname;
    const sort = current.searchParams.get('sort_by') || navigation.getAttribute('data-citadel-shop-all-default-sort');
    const path = readShopAllPath(current.href);
    const selectedSystem = systemControls.find(({ tag }) => path?.tags.includes(tag));
    const states = [[all, atRoot && sort !== 'best-selling'], [best, atRoot && sort === 'best-selling']];
    systemControls.forEach(({ link, tag }) => states.push([link, path?.tags.includes(tag)]));
    subcategoryControls.forEach(({ link, system, tag }) => {
      const active = path?.tags.includes(system.tag) && path.tags.includes(tag);
      const tags = active ? system.tag : [system.tag, tag].sort().join('+');
      link.setAttribute('data-citadel-shop-all-destination', `${path.cataloguePath}/${tags}`);
      states.push([link, active]);
    });
    navigation.querySelector('.sd--tabs--filter')?.setAttribute('data-active-system',
      selectedSystem?.link.getAttribute('data-tab') || '');
    states.forEach(([link, active]) => {
      link.classList.toggle('active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  };

  const resetBindings = new Map();
  const onReset = event => {
    // Buttons retain native keyboard activation; the legacy Clear All item is an li.
    if (event.type === 'keydown' &&
        (event.currentTarget.tagName !== 'LI' || !['Enter', ' '].includes(event.key))) return;
    event.preventDefault();
    window.location.assign(buildShopAllResetUrl(window.location.href));
  };
  const bindResets = () => {
    if (typeof navigation.clearAllListener !== 'function') return;
    const controls = [navigation.querySelector('.navigation--active-clear'),
      document.querySelector('.filter--root .filter--button[data-type="reset"]')];
    controls.filter(Boolean).forEach(control => {
      if (resetBindings.has(control)) return;
      // Blockshop stores its own paired listeners here. Remove only those exact
      // references; keep all other click listeners and normal event propagation.
      const nativeClick = control.namespaces?.click;
      const nativeKeydown = control.namespaces?.keydown;
      if (!nativeClick || nativeClick !== nativeKeydown) return;
      control.removeEventListener('click', nativeClick);
      control.removeEventListener('keydown', nativeKeydown);
      control.addEventListener('click', onReset);
      control.addEventListener('keydown', onReset);
      resetBindings.set(control, { nativeClick, nativeKeydown });
    });
  };
  const onThemeLoaded = () => {
    preserveNativeContext();
    bindResets();
    bindPriceHandling();
  };

  const onNavigationReload = event => {
    if (document.querySelector('#collection_root [data-body-grid]') !== grid ||
        document.querySelector('[data-citadel-shop-all]') !== navigation ||
        document.querySelector('[data-citadel-shop-all-count]') !== count) {
      installShopAll();
      return;
    }
    refreshCount();
    refreshSort();
    if (event?.type === 'pageshow') { priceKeys = readShopAllPriceKeys(window.location.href); priceSeeded = false; }
    refreshViews();
    refreshLinks();
    refreshPagination();
    preserveNativeContext();
    bindResets();
    bindPriceHandling();
  };

  const onLinkClick = event => {
    const link = event.target.closest('[data-citadel-shop-all-destination]');
    if (link && navigation.contains(link)) {
      link.href = buildShopAllUrl(link.getAttribute('data-citadel-shop-all-destination'), window.location.href,
        link.getAttribute('data-citadel-shop-all-sort'));
    }
  };

  const onPrimaryFocus = event => {
    const link = event.target.closest('.sd--tabs--main > .sd--tab--btn');
    if (!link || !navigation.contains(link)) return;
    const rail = link.parentElement;
    if (rail.scrollWidth <= rail.clientWidth) return;
    const bounds = rail.getBoundingClientRect();
    const pill = link.getBoundingClientRect();
    // Match the existing 5px rail padding: 2px outline plus 3px offset.
    const delta = pill.left < bounds.left + 5
      ? pill.left - bounds.left - 5
      : Math.max(0, pill.right - bounds.right + 5);
    if (delta) rail.scrollBy({ left: delta, behavior: 'instant' });
  };

  const observer = new MutationObserver(refreshCount);
  observer.observe(grid, { childList: true });
  navigation.addEventListener('click', onLinkClick, true);
  navigation.addEventListener('focusin', onPrimaryFocus);
  window.addEventListener('theme:navigation:reloaded', onNavigationReload);
  window.addEventListener('theme:loaded', onThemeLoaded);
  window.addEventListener('pageshow', onNavigationReload);
  window.addEventListener('theme:priceRange:renderActive', onPriceRender);
  preserveNativeContext();
  refreshCount();
  refreshSort();
  refreshViews();
  refreshLinks();
  refreshPagination();
  bindResets();
  bindPriceHandling();
  installation = {
    navigation, grid, count,
    rebind() { bindPriceHandling(); },
    dispose() {
      observer.disconnect();
      disposeNumericInputs();
      if (document.querySelector('[data-citadel-shop-all]') !== navigation) {
        nativeWindowBindings.forEach((listener, type) => window.removeEventListener(type, listener));
        navigation.abort_controller?.abort();
      }
      navigation.removeEventListener('click', onLinkClick, true);
      navigation.removeEventListener('focusin', onPrimaryFocus);
      window.removeEventListener('theme:navigation:reloaded', onNavigationReload);
      window.removeEventListener('theme:loaded', onThemeLoaded);
      window.removeEventListener('pageshow', onNavigationReload);
      window.removeEventListener('theme:priceRange:renderActive', onPriceRender);
      priceForm?.removeEventListener('change', onPriceIntent, true);
      priceForm?.removeEventListener('mousedown', onPriceIntent, true);
      priceForm?.removeEventListener('touchstart', onPriceIntent, true);
      if (navigation.getAjaxUrl === getPriceAwareAjaxUrl) navigation.getAjaxUrl = nativeGetAjaxUrl;
      priceBindings.forEach(({ nativeClick, nativeKeydown }, control) => {
        control.removeEventListener('click', onRemovePrice);
        control.removeEventListener('keydown', onRemovePrice);
        control.addEventListener('click', nativeClick);
        control.addEventListener('keydown', nativeKeydown);
      });
      resetBindings.forEach(({ nativeClick, nativeKeydown }, control) => {
        control.removeEventListener('click', onReset);
        control.removeEventListener('keydown', onReset);
        control.addEventListener('click', nativeClick);
        control.addEventListener('keydown', nativeKeydown);
      });
    }
  };
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installShopAll, { once: true });
  } else {
    installShopAll();
  }
  document.addEventListener('shopify:section:load', installShopAll);
}
